import { defineStore } from 'pinia';
import { Company } from '@/api/interfaces/company';
import { loadUserCompanies } from '@/api';
import { markCompaniesInitialized, notifyCompanyContextReady } from '@/utils/initialization';
import { isValidUuidString } from '@/utils/uuid';

interface CompaniesState {
  companies: Company[];
  selectedCompany: Company | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  isInitialized: boolean; // Флаг завершения инициализации
}

const SELECTED_COMPANY_KEY = 'selectedCompanyId';

export const useCompaniesStore = defineStore('companies', {
  state: (): CompaniesState => ({
    companies: [],
    selectedCompany: null,
    isLoading: false,
    isLoaded: false,
    error: null,
    isInitialized: false,
  }),

  getters: {
    selectedCompanyId: (state): string | null => {
      return state.selectedCompany?.id || null;
    },

    hasSelectedCompany: (state): boolean => {
      return state.selectedCompany !== null;
    },

    hasMultipleCompanies: (state): boolean => {
      return state.companies.length > 1;
    },

    hasSingleCompany: (state): boolean => {
      return state.companies.length === 1;
    },

    hasNoCompanies: (state): boolean => {
      return state.companies.length === 0;
    },

    needsCompanySelection: (state): boolean => {
      return !state.selectedCompany && state.companies.length > 1;
    },

    canAutoSelectCompany: (state): boolean => {
      return !state.selectedCompany && state.companies.length === 1;
    },

    hasError: (state): boolean => {
      return state.error !== null;
    },

    needsLoading: (state): boolean => {
      return !state.isLoaded && !state.isLoading;
    },

    /**
     * Готовность tenant-контекста: есть выбранная компания из успешно загруженного списка.
     * Нельзя возвращать true при companies.length===0 без проверки error: при ошибке /me/companies
     * catch обнуляет список — иначе монтируется router-view без X-Company-ID → 428 на каждом API.
     */
    isCompanyContextReady: (state): boolean => {
      if (!state.isLoaded || state.isLoading) {
        return false;
      }
      if (state.error) {
        return false;
      }
      if (state.companies.length === 0) {
        return false;
      }
      return state.selectedCompany !== null;
    },
  },

  actions: {
    maybeNotifyCompanyContextReady(): void {
      if (this.isCompanyContextReady) {
        notifyCompanyContextReady();
        return;
      }
      // Разблокировать waitForCompanyContextReady при терминальных состояниях (без tenant), иначе deadlock
      if (this.isLoaded && !this.isLoading && (this.error || this.companies.length === 0)) {
        notifyCompanyContextReady();
      }
    },

    // Синхронное восстановление компании из localStorage (без API запроса)
    restoreSelectedCompanyFromLocalStorage(): void {
      const savedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);

      if (savedCompanyId && isValidUuidString(savedCompanyId)) {
        // Создаем временный объект компании с сохраненным ID
        // Реальные данные компании будут загружены позже в loadCompanies
        this.selectedCompany = { id: savedCompanyId } as Company;
      } else if (savedCompanyId) {
        localStorage.removeItem(SELECTED_COMPANY_KEY);
      }
    },

    // Быстрая инициализация - только из localStorage
    quickInitialize(): void {
      if (this.isInitialized) {
        return;
      }

      this.restoreSelectedCompanyFromLocalStorage();
      this.isInitialized = true;

      // Уведомляем об завершении инициализации
      markCompaniesInitialized();

    },

    async loadCompanies(): Promise<void> {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const companies = await loadUserCompanies();
        this.companies = companies;

        // Попытаемся восстановить выбранную компанию из localStorage с проверкой существования
        await this.restoreSelectedCompanyFromStorage();

        // Если компания не выбрана и есть только одна - выбираем автоматически
        if (this.canAutoSelectCompany) {
          this.selectCompany(this.companies[0]);
        }
      } catch (error) {
        console.error('Failed to load companies:', error);
        this.error = error instanceof Error ? error.message : 'Ошибка загрузки компаний';
        this.companies = [];
      } finally {
        this.isLoading = false;
        this.isLoaded = true;
        this.maybeNotifyCompanyContextReady();
      }
    },

    async restoreSelectedCompanyFromStorage(): Promise<void> {
      const savedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);

      if (savedCompanyId && !isValidUuidString(savedCompanyId)) {
        localStorage.removeItem(SELECTED_COMPANY_KEY);
        this.selectedCompany = null;
        return;
      }

      if (savedCompanyId && this.companies.length > 0) {
        const savedCompany = this.companies.find(c => c.id === savedCompanyId);
        if (savedCompany) {
          this.selectedCompany = savedCompany;
        } else {
          // Компания не найдена - возможно пользователь потерял к ней доступ
          console.warn('Companies: Company from localStorage not found in available companies, clearing storage');
          localStorage.removeItem(SELECTED_COMPANY_KEY);
          this.selectedCompany = null;
        }
      } else if (savedCompanyId) {
        console.warn('Companies: Company ID in localStorage but no companies loaded yet');
      }
    },

    selectCompany(company: Company): void {
      this.selectedCompany = company;
      localStorage.setItem(SELECTED_COMPANY_KEY, company.id);
      this.maybeNotifyCompanyContextReady();
    },

    clearSelectedCompany(): void {
      this.selectedCompany = null;
      localStorage.removeItem(SELECTED_COMPANY_KEY);
    },

    async refreshCompanies(): Promise<void> {
      this.isLoaded = false;
      await this.loadCompanies();
    },

    clearError(): void {
      this.error = null;
    },

    // Для принудительной установки выбранной компании (например, при обработке 428 ошибки)
    forceSelectCompany(companyId: string): boolean {
      const company = this.companies.find(c => c.id === companyId);
      if (company) {
        this.selectCompany(company);
        return true;
      }
      return false;
    },
  },
}); 